import { pluginLucideIcon } from './pluginIcons';
import type { PluginUiListing } from './types';
import type { NavEntry } from '../components/shell/navEntry';

/** Where a plugin's settings section lives. Sections are pages of the plugin's own world — Settings is
 *  core-only — so this is the one rule that decides their address, shared by the menu and by every
 *  redirect that still points at the old Settings deck id.
 *
 *  A plugin whose whole UI is one settings section lives at `/p/<plugin>`: spelling it out as
 *  `/p/skills/settings/skills` repeats the plugin's own name back at the reader. The host route still
 *  serves the explicit form, so existing links and multi-section plugins keep working. */
export function pluginSectionHref(p: PluginUiListing, settingId: string): string {
  const base = `/p/${p.name}`;
  return p.nav.length === 0 && p.settings.length === 1 ? base : `${base}/settings/${settingId}`;
}

/** Address a plugin-contributed section that lives inside Settings → Plugins → plugin detail. It remains
 *  one internal tab of that plugin workspace; callers must not promote the section to a Settings category. */
export function pluginDetailSectionHref(pluginName: string, settingId: string): string {
  const query = new URLSearchParams({ cat: 'plugins', plugin: pluginName });
  return `/settings?${query.toString()}#plugin-section:${encodeURIComponent(settingId)}`;
}

/** The settings section a plugin route addresses, resolved in ONE place for the page route and the
 *  overlay. `explicitlyRequestedSetting` is `/p/<plugin>/settings/<id>`; `bareSoleSetting` is the bare
 *  `/p/<plugin>` route when the section is the plugin's whole UI (no nav pages, exactly one settings
 *  section), so its address does not repeat its own name. Both keep resolving whatever the section's
 *  `placement` is — placement decides what the app advertises, not what it can render — and callers
 *  decide what a `pluginDetail` placement means for them (the host route redirects it into the deck). */
export function resolvePluginSettingsRoute(
  entry: PluginUiListing | undefined,
  rest: string[],
): {
  explicitlyRequestedSetting: PluginUiListing['settings'][number] | undefined;
  bareSoleSetting: PluginUiListing['settings'][number] | undefined;
} {
  const explicitlyRequestedSetting = rest[0] === 'settings' && rest.length === 2
    ? entry?.settings.find((setting) => setting.id === rest[1])
    : undefined;
  const bareSoleSetting = rest.length === 0 && entry?.nav.length === 0 && entry?.settings.length === 1
    ? entry.settings[0]
    : undefined;
  return { explicitlyRequestedSetting, bareSoleSetting };
}

/** Reserve exact user-page paths before bundle loading. This metadata only selects discovery; the
 * server's targeted response, never the hint, authorizes loading that page's separate assets. */
export function isPluginUserPage(entry: PluginUiListing | undefined, route: string): boolean {
  return entry?.userPages?.includes(route) === true;
}

/** Map the /plugins/ui listing to sidebar worlds: one world per plugin, its first page as the world's
 *  face and the rest as sub-items.
 *
 *  A plugin's pages are BOTH its `web.nav` pages and its `web.settings` sections: the host route serves a
 *  settings section as a standalone page at `/p/<plugin>/settings/<id>`, so a plugin whose whole UI is a
 *  settings section is a plugin with a page — it just used to be reachable only through the Settings deck.
 *  The deck keeps its sections; this only adds the direct way in. A plugin contributing neither claims no
 *  menu space. Pure so the mapping is unit-testable.
 *
 *  A section placed `pluginDetail` is deliberately absent from this mapping: it configures a capability
 *  the assistant already ships rather than opening a world of its own, so it belongs where the operator
 *  installs and tunes that plugin. Filtering HERE covers the sidebar and the command palette at once —
 *  they read the same entries — and leaves the section's own address untouched for anything that links
 *  to it. A plugin whose every surface is placed there claims no menu space at all. */
export function pluginNavEntries(listing: PluginUiListing[]): NavEntry[] {
  return listing.flatMap((p) => {
    const base = `/p/${p.name}`;
    const pages = [
      ...p.nav.map((item) => ({
        href: item.route ? `${base}/${item.route}` : base,
        label: item.label,
        icon: item.icon,
      })),
      ...p.settings.filter((s) => s.placement !== 'pluginDetail')
        .map((s) => ({ href: pluginSectionHref(p, s.id), label: s.label, icon: s.icon })),
    ];
    const first = pages[0];
    if (!first) return [];
    return [{
      id: `plugin-${p.name}`,
      href: first.href,
      // A plugin contributing several peer pages names its own world; without a name the world borrows
      // the first page's, which then reads as if that page stood over its siblings.
      label: p.label ?? first.label,
      icon: pluginLucideIcon(first.icon),
      ...(p.badge ? { badge: p.badge } : {}),
      activeRoutes: [base],
      subItems: pages.length > 1
        ? pages.map((page, i) => ({
          id: `plugin-${p.name}-${i}`,
          href: page.href,
          label: page.label,
          icon: pluginLucideIcon(page.icon),
        }))
        : undefined,
    }];
  });
}
